import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { ShortNote } from '../../../../../api/homeShortsFeeds/types';
import { analyzeCsv, CsvAnalysisResult, ShortRow } from '../utils';

interface Props {
  open: boolean;
  feedName: string;
  currentIds: string[];
  shortsById: Map<string, ShortRow>;
  onClose: () => void;
  /** receives the new ranked ids and the notes to merge into the draft (overwriting per id) */
  onApply: (ids: string[], notes: Record<string, ShortNote>) => void;
}

type MissingMode = 'append' | 'remove';

// Reads a CSV client-side and previews the result before it touches the draft.
const CsvImportModal = ({ open, feedName, currentIds, shortsById, onClose, onApply }: Props) => {
  const [fileName, setFileName] = useState('');
  const [text, setText] = useState<string | undefined>(undefined);
  const [readError, setReadError] = useState('');
  const [missingMode, setMissingMode] = useState<MissingMode>('append');

  useEffect(() => {
    if (open) {
      setFileName('');
      setText(undefined);
      setReadError('');
      setMissingMode('append');
    }
  }, [open]);

  const result: CsvAnalysisResult | undefined = useMemo(
    () => (text === undefined ? undefined : analyzeCsv(text, shortsById, currentIds)),
    [text, shortsById, currentIds],
  );
  const analysis = result && 'analysis' in result ? result.analysis : undefined;

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setFileName(file.name);
      setReadError('');
      setText(await file.text());
    } catch {
      setText(undefined);
      setReadError('Could not read this file.');
    }
  };

  const apply = () => {
    if (!analysis) {
      return;
    }
    const importedSet = new Set(analysis.importedIds);
    const kept = missingMode === 'append' ? currentIds.filter((id) => !importedSet.has(id)) : [];
    onApply([...analysis.importedIds, ...kept], analysis.notes);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Import CSV into “{feedName}”</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <div>
            <input type='file' accept='.csv,text/csv' onChange={onFileChange} aria-label='Choose CSV file' />
            <Typography variant='caption' color='text.secondary' component='p' sx={{ mt: 0.5 }}>
              Required column: <code>short_id</code>. Optional: <code>position</code> (rows are sorted by it, otherwise
              file order is kept), <code>worth</code>, <code>why</code> and <code>gold_star</code> (editorial notes).
              Other columns are ignored. Nothing is saved until you press “Save changes”.
            </Typography>
          </div>

          {readError && <Alert severity='error'>{readError}</Alert>}
          {result && 'error' in result && <Alert severity='error'>{result.error}</Alert>}

          {analysis && (
            <Stack spacing={1.5} aria-live='polite'>
              <Typography variant='body2'>
                <strong>{fileName}</strong>: {analysis.importedIds.length} shorts matched.
              </Typography>
              {Object.keys(analysis.notes).length > 0 && (
                <Typography variant='body2'>
                  {Object.keys(analysis.notes).length} notes (worth/why/gold star) will be updated.
                </Typography>
              )}
              {analysis.unknownIds.length > 0 && (
                <Alert severity='warning'>
                  {analysis.unknownIds.length} unknown short_id (skipped): {analysis.unknownIds.slice(0, 10).join(', ')}
                  {analysis.unknownIds.length > 10 ? ` and ${analysis.unknownIds.length - 10} more` : ''}
                </Alert>
              )}
              {analysis.duplicateIds.length > 0 && (
                <Alert severity='info'>
                  {analysis.duplicateIds.length} duplicate short_id (first occurrence kept):{' '}
                  {analysis.duplicateIds.slice(0, 10).join(', ')}
                </Alert>
              )}
              {analysis.missingIds.length > 0 && (
                <FormControl>
                  <FormLabel id='csv-missing-label'>
                    {analysis.missingIds.length} shorts in this feed are not in the CSV
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby='csv-missing-label'
                    value={missingMode}
                    onChange={(e) => setMissingMode(e.target.value as MissingMode)}>
                    <FormControlLabel value='append' control={<Radio />} label='Append them after the imported rows' />
                    <FormControlLabel value='remove' control={<Radio />} label='Remove them from the feed' />
                  </RadioGroup>
                </FormControl>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={apply} disabled={!analysis || analysis.importedIds.length === 0}>
          Apply to draft
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportModal;
